import { BlankEditorResult } from '@doltech/ui/lib/figma/BlankEditor/Result/BlankEditorResult';
import { CollapseExpandPanel } from '@doltech/ui/lib/figma/CollapseExpandPanel/CollapseExpandPanel';
import { figmaColors } from '@doltech/ui/lib/figma/colors';
import { Box } from '@doltech/ui/lib/figma/Common/Box';
import { TestModeEnum } from '@doltech/utils/lib/constants';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { Heading } from '../../../student-do-test-questions/CompletionNoHintAnswer';
import { ResultFlowChartQuestionInstance } from '../../model/Question/result-flowchart-question.store';
import { ExtraQuestionProps } from './BaseQuestion';

interface FlowChartQuestionResultProps extends React.HTMLAttributes<HTMLDivElement> {
  data: ResultFlowChartQuestionInstance & ExtraQuestionProps;
  testMode: TestModeEnum;
}

export const Main = styled(Box)``;

const QuestionList = styled.div`
  display: grid;
  gap: 16px;
  .sentence-container {
    display: grid;
    gap: 16px;
  }
  .single-sentence {
    display: grid;
    gap: 8px;
  }
`;

export const BlankEditorContainer = styled.div`
  align-items: center;
  background: ${figmaColors.gray100};
  border: 1px solid ${figmaColors.grayMedium};
  box-sizing: border-box;
  border-radius: 8px;
  display: flex;
  padding: 10px 12px;
  > div {
    background: ${figmaColors.gray100};
    border: unset;
    box-shadow: unset;
    padding: unset;
  }
`;

export const StepContainer = styled.div`
  font-weight: bold;
  font-size: 10px;
  line-height: 12px;
  color: ${figmaColors.text3};
`;

export const FlowChartQuestionResult = observer((props: FlowChartQuestionResultProps) => {
  const { data, testMode } = props;
  const { no, heading, sentences, index } = data;
  const [expand, setExpand] = React.useState(false);
  const defaultTitle = `STEP ${index + 1}`;

  const questionCollapsed = () => {
    const sentenceIndex = sentences.findIndex((it) =>
      it.blanks.map((blank) => blank.key).includes(index)
    );
    const { id, editorValueJS, blanks, title } = sentences[sentenceIndex];
    const sentenceTitle = title || defaultTitle;

    return (
      <QuestionList>
        <Heading>{heading}</Heading>
        <StepContainer>{sentenceTitle}</StepContainer>
        <BlankEditorContainer>
          <BlankEditorResult
            key={id}
            value={editorValueJS}
            blanks={blanks}
            currentSelectedIndex={index}
            testMode={testMode}
          />
        </BlankEditorContainer>
      </QuestionList>
    );
  };

  const questionExpanded = () => {
    return (
      <QuestionList>
        <Heading>{heading}</Heading>
        {sentences.map((sentence, sentenceIndex) => {
          const defaultSentenceTitle = `Step ${sentenceIndex + 1}`;

          const { id, editorValueJS, blanks, title } = sentence;
          const sentenceTitle = title || defaultSentenceTitle;

          return (
            <React.Fragment key={id}>
              <StepContainer>{sentenceTitle}</StepContainer>
              <BlankEditorContainer>
                <BlankEditorResult
                  testMode={testMode}
                  key={id}
                  value={editorValueJS}
                  blanks={blanks}
                />
              </BlankEditorContainer>
            </React.Fragment>
          );
        })}
      </QuestionList>
    );
  };

  return (
    <Main
      as={CollapseExpandPanel}
      key={no}
      className="dol-question flow-chart-answer"
      onToggleCollapse={() => setExpand((oldExpandValue) => !oldExpandValue)}
      expand={expand}
      collapsedSection={questionCollapsed()}
      expandedSection={questionExpanded()}
    />
  );
});
